/**
 * Router -- Minimal hash-based SPA router.
 * Routes map hash fragments to view render functions.
 *
 * @module router
 */

export class Router {
  #routes = new Map();
  #fallback = null;
  #container = null;

  constructor(container) {
    this.#container = container;
    window.addEventListener('hashchange', () => this.#resolve());
  }

  route(hash, handler) {
    this.#routes.set(hash, handler);
    return this;
  }

  fallback(handler) {
    this.#fallback = handler;
    return this;
  }

  start() {
    this.#resolve();
  }

  navigate(hash) {
    window.location.hash = hash;
  }

  get currentRoute() {
    return window.location.hash || '#/';
  }

  #resolve() {
    const hash = this.currentRoute;
    // Una ruta con ?query (la API del MCP manda a #/oauth/autorizar?solicitud=<uuid>, MCP-NEXO-OAUTH-T1) se busca
    // tambien sin la query. Va DESPUES del match exacto: ninguna ruta que hoy funciona cambia de vista.
    let handler = this.#routes.get(hash) || (hash.includes('?') ? this.#routes.get(hash.split('?')[0]) : undefined);

    if (!handler) {
      for (const [pattern, h] of this.#routes) {
        if (pattern.includes(':') && this.#matchPattern(pattern, hash)) {
          handler = h;
          break;
        }
      }
    }

    handler = handler || this.#fallback;
    if (handler && this.#container) {
      // Limpiar listeners registrados por la vista anterior
      if (typeof this.#container._cleanupBusinessListener === 'function') {
        this.#container._cleanupBusinessListener();
        delete this.#container._cleanupBusinessListener;
      }
      this.#container.innerHTML = '';
      handler(this.#container, this);
    }
  }

  #matchPattern(pattern, hash) {
    const patternParts = pattern.split('/');
    const hashParts = hash.split('/');
    if (patternParts.length !== hashParts.length) return false;
    return patternParts.every((p, i) => p.startsWith(':') || p === hashParts[i]);
  }
}
